// A small promisified IndexedDB wrapper. The app keeps a full local mirror of
// the collection so review works with no network at all, and queues answers in
// an outbox that drains when connectivity returns.

import type { Card, Note, Review, Source } from "./types";

const DB_NAME = "langlearn";
const DB_VERSION = 1;

export type StoreName =
  | "sources"
  | "notes"
  | "cards"
  | "outboxReviews"
  | "outboxCards"
  | "meta";

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("sources")) {
        db.createObjectStore("sources", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("notes")) {
        const notes = db.createObjectStore("notes", { keyPath: "id" });
        notes.createIndex("lang", "lang");
        notes.createIndex("source_id", "source_id");
      }
      if (!db.objectStoreNames.contains("cards")) {
        const cards = db.createObjectStore("cards", { keyPath: "id" });
        cards.createIndex("lang", "lang");
        cards.createIndex("note_id", "note_id");
      }
      if (!db.objectStoreNames.contains("outboxReviews")) {
        db.createObjectStore("outboxReviews", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("outboxCards")) {
        db.createObjectStore("outboxCards", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

function run<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  body: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const request = body(tx.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

export function getAll<T>(store: StoreName): Promise<T[]> {
  return run<T[]>(store, "readonly", (s) => s.getAll() as IDBRequest<T[]>);
}

export function get<T>(store: StoreName, key: string): Promise<T | undefined> {
  return run<T | undefined>(store, "readonly", (s) => s.get(key) as IDBRequest<T | undefined>);
}

export function put<T>(store: StoreName, value: T): Promise<unknown> {
  return run(store, "readwrite", (s) => s.put(value as unknown as object));
}

export function remove(store: StoreName, key: string): Promise<unknown> {
  return run(store, "readwrite", (s) => s.delete(key));
}

/** Bulk write in a single transaction — used by every sync pull. */
export async function putMany<T>(store: StoreName, values: T[]): Promise<void> {
  if (values.length === 0) return;
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    const objectStore = tx.objectStore(store);
    for (const value of values) objectStore.put(value as unknown as object);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function removeMany(store: StoreName, keys: string[]): Promise<void> {
  if (keys.length === 0) return;
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    const objectStore = tx.objectStore(store);
    for (const key of keys) objectStore.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function clearAll(): Promise<void> {
  const db = await open();
  const stores: StoreName[] = ["sources", "notes", "cards", "outboxReviews", "outboxCards", "meta"];
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(stores, "readwrite");
    for (const store of stores) tx.objectStore(store).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// --- Typed convenience wrappers ---------------------------------------------

export const loadSources = () => getAll<Source>("sources");
export const loadNotes = () => getAll<Note>("notes");
export const loadCards = () => getAll<Card>("cards");
export const loadOutboxReviews = () => getAll<Review>("outboxReviews");
export const loadOutboxCards = () => getAll<Card>("outboxCards");

interface MetaRow<T> {
  key: string;
  value: T;
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await get<MetaRow<T>>("meta", key);
  return row ? row.value : fallback;
}

export function setMeta<T>(key: string, value: T): Promise<unknown> {
  return put<MetaRow<T>>("meta", { key, value });
}
